import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { LoggingMessageService } from 'src/app/core/services/logging-message.service';
import { environment } from 'src/environments/environment';
import { StorageService } from 'src/app/core/services/storage.service';
import { DataResult } from 'src/app/core/models/data-result.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserSSO } from '../site-management/site-coordinator/models/userSSO.model';

@Injectable({
  providedIn: 'root'
})
export class FederationService implements OnInit, OnDestroy {

  private _BASE_URL = environment.FederationBusinessApi;
  private _LOCAL_API = environment.LocalJsonApi;  
  private _BASE_TOKEN_URL: string = environment.TokenServiceApi;
  private _Local_Federation_Check = 'federation-check.json';  
  private $subscriptions: Subscription = new Subscription();
  private _NO_DATA = { message: '' };

  constructor(private http: HttpClient, private logger: LoggingMessageService, private storageService: StorageService, private authService: AuthService) { }
  ngOnInit(): void {

  }
  public getFederationToken(): Observable<DataResult> {
    if (environment.UseBusinessServerForFederationToken) {
        return this.getFederationTokenFromBusinessApi();
    } else {
        return this.getFederationTokenFromLocalApi();
    }
  }

  private getFederationTokenFromLocalApi(): Observable<DataResult> {
    return of(null);
  }

  private getFederationTokenFromBusinessApi(): Observable<DataResult> {
      const webmethod = '/FederationToken';
      const media = 'application/json';
      const uri = `${this._BASE_TOKEN_URL}${webmethod}`;

                //    // DONNOT CHECK IN - LOCAL ONLY - For local development, return a dummy token
                //    if (environment.environment == 'Local') {
                //     console.log('Development mode - returning dummy token');
                //     return of(new DataResult(true, 200, 'OK', { Token: 'dummy-token' }));
                // }
            

      this.logger.debug('federation.service', 'getFederationTokenFromBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Requesting federation token: calling url: ${uri}', '');

      return this.http.get<any>(
          uri,
          {
              headers: { Accept: media },
              observe: 'response'
          }
      )
          .pipe(
              map(response => {
                  let result: DataResult;
                  if (response instanceof HttpResponse) {
                      const respBody: any = response.body;
                      if (response.status === 200) {
                          result = new DataResult(response.ok, response.status, response.statusText, respBody);
                          this.logger.info('federation.service', 'getFederationTokenFromBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Successfully obtained federation token', '');
                      } else if (response.status === 401) {
                          result = new DataResult(false, 401, response.statusText, respBody?.message || 'Unauthorized');
                          this.logger.error('federation.service', 'getFederationTokenFromBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Unauthorized access when obtaining federation token', 'Status: ${response.status}}');
                      } else {
                          result = new DataResult(false, 400, response.statusText, respBody.message);
                          this.logger.error('federation.service', 'getFederationTokenFromBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Bad request when obtaining federation token', response.statusText);
                      }
                  }
                  if (response instanceof HttpErrorResponse) {
                      result = new DataResult(false, 500, response.statusText, response.message);
                      this.logger.error('federation.service', 'getFederationTokenFromBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Server error when obtaining federation token', response.message);
                  }
                  return result;
              }),
              catchError(error => {
                  this.logger.error('federation.service', 'getFederationFromBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Error caught', error.message);
                  const message = this.getErrorMessage(error);
                  return this.handleError(`getFederationFromBusinessApi failed`, message);
               }),
              take(1),
          );
  }

  private getErrorMessage(error: any): string {
    let message = error.message;
    if ((error.error) && (error.error.message)) {
        message = error.error.message;
    }
    return message;
}

public FederationCall(selectedUserData: any): Observable<DataResult> {
  if (environment.UseBusinessServerForFederation) {
      return this.FederationBusinessApi(selectedUserData);
  } else {
      return this.FederationLocalApi();
  }
}


  private FederationBusinessApi(selectedUserData: any): Observable<DataResult> {
    const webmethod = '/UserUAM/ExportUsersToUamNoTLP';
    const media = 'application/json';
    const uri = `${this._BASE_URL}${webmethod}`;
    
    // Get authUserId from the decoded token
    const authUserId = this.authService.getAuthUserId();
    //console.log('Using authUserId from Token in decoded getAccessToken:', authUserId);
    
    // Construct the payload from UserSSO model.
    const user: UserSSO = 
      {
        //tlpID: sysId,
        userEmail: selectedUserData.userEmail,
        userFirstName: selectedUserData.userFirstName,
        userLastName: selectedUserData.userLastName,
        userPhone: selectedUserData.userPhone,
        roleID: selectedUserData.roleID,
        roleName: selectedUserData.role,
        isUserActive: selectedUserData.activeYN,
        authUserID:  selectedUserData.authUserID 
      };
    
    const users = [user];

    const params = {
      migrationSourceID: 7,
      loggedUserID: authUserId,
      systemID: 0,
      facilityID: selectedUserData.facilityID
    };
    
    return this.http.post<any>(
      uri, users,
      {
        headers: { Accept: media }, 
        params,
        observe: 'response'})
    .pipe(
      map(response => {
        let result: DataResult;
        if (response instanceof HttpResponse) {
          const respBody: any = response.body;
          if (response.status === 200) {
            result = new DataResult(true, response.status, response.statusText, respBody);
            this.logger.info('federation.service', 'FederationBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Exported users to UAM', `Response: ${JSON.stringify(respBody)}`);
          } else {
            result = new DataResult(false, response.status, response.statusText, respBody.message);
            this.logger.info('federation.service', 'FederationBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Failed to export users to UAM', `Status: ${response.status}, Response: ${JSON.stringify(respBody)}`);
          }
        }        
        this.logger.info('federation.service', 'FederationBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Exported users to UAM Result', `Status: ${response.status}, Resule: ${JSON.stringify(result)}`);
        return result;
      }),
      catchError((error) => {
        this.logger.error('federation.service', 'FederationBusinessApi', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, 'Error calling ExportUsersToUAM', `Error: ${JSON.stringify(error)}`);
        return of(new DataResult(false, 500, 'Service Unavailable error', null));
      })
    );
  }

  private FederationLocalApi(): Observable<DataResult> {
    const uri = `${this._LOCAL_API}${this._Local_Federation_Check}`;
  
      return this.http.get<any>(
        uri, { observe: 'response' }
      )
        .pipe(
          map(response => {
            let result: any;
            if (response instanceof HttpResponse) {
              if (response.status === 200) {
                result = response.body;
              }
            }
            if (response instanceof HttpErrorResponse) {
              result = this._NO_DATA;
            }
  
            return result;
          }),
          catchError(error => {
            return this.handleError(`getLocalUsers failed`, error);
          }),
          take(1)
        );
  }

  private handleError(operation: string, error: any){
    let result: any;
    const response = this._NO_DATA;
    this.logger.warn('user-management.service', 'handleError', this.authService.SelectedSubscription, this.authService.AvailableSubscriptionsString, `Operation:${operation}`, error.message);
    response.message = error;
    const errorResult = new DataResult(false, 503, 'Service Unavailable error', response);
    result = errorResult;

    return of(result);
  }

  unsubscribe() {
    if (this.$subscriptions) {
        this.$subscriptions.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe();
}

}
